import React, { useState, useEffect, useContext } from 'react';
import '../../App.css';
import './data-management.css';
import { useTranslation } from 'react-i18next';
import { addLineBreaks } from '../../utils/functions';
import { useAppDispatch, useAppSelector } from '../../app/hooks';

// icons
import downArrowDarkIcon from '../../icons/down-arrow-dark.256.png';
import upArrowDarkIcon from '../../icons/up-arrow-dark.256.png';
import downArrowLightIcon from '../../icons/down-arrow-light.256.png';
import upArrowLightIcon from '../../icons/up-arrow-light.256.png';
import binLightIcon from '../../icons/bin-light.256.png';
import binDarkIcon from '../../icons/bin-dark.256.png';
import refreshIcon from '../../icons/refresh.512.png';

// types
import {
  JobItemType,
  JobType,
  SiteStorageArea,
  ComputeServiceType,
  FileBrowserPage,
  GatewayLogType,
  ToolType,
  toolTypeToPath,
} from '../../utils/types';

// functions
import { APIPrefix, siteName } from '../../utils/functions';

// classes
import { DisplayFileSize } from '../../utils/functions';
import Scrollbox from '../../tools/scrollbox';
import ComputeSession from '../compute-tiles/compute-session';
import MiniButton from '../../tools/mini-button';
import StatusIndicator from '../../tools/status-indicator';
import PleaseWait from '../../tools/please-wait';
import {
  Event,
  EventType,
  EventTarget,
  InitiateComputeSearchParams,
  InitiateDMSearchParams,
} from '../../utils/events';
import { useNavigate } from 'react-router-dom';
import { handleEvent } from '../../app-actions';

//	--------------------------------------------------------------------------
//
//	T Y P E S
//
//	--------------------------------------------------------------------------

//	--------------------------------------------------------------------------
//
//	P R O P E R T I E S
//
//	--------------------------------------------------------------------------

//	--------------------------------------------------------------------------
//
//	H T M L   C U S T O M   C O M P O N E N T S
//
//	--------------------------------------------------------------------------

//	--------------------------------------------------------------------------
//
//	C L A S S   D E F I N I T I O N
//
//	--------------------------------------------------------------------------

export default function JobStatus(props: {
  dataManagementJobs: JobType[];
  jobsLoading: boolean;
  logEntries: GatewayLogType[];
  logsFocusRow: React.MutableRefObject<string | undefined>;
  transparentBackground?: boolean;
}) {
  //	--------------------------------------------------------------------------
  //
  //	R E D U X   S T A T E   H A N D L I N G
  //
  //	--------------------------------------------------------------------------

  // global state variables.
  const sG_darkMode: boolean = useAppSelector((state) => state.global.darkMode);
  const sG_loggedIn: boolean | undefined = useAppSelector(
    (state) => state.global.loggedIn,
  );

  // data state variables
  const sD_storageAreas: SiteStorageArea[] = useAppSelector(
    (state) => state.data.storageAreas,
  );

  // hold the list of compute services from the site-capabilities catalog.
  const sD_computeServices: ComputeServiceType[] = useAppSelector(
    (state) => state.data.computeServices,
  );

  //	--------------------------------------------------------------------------
  //
  //	D E C L A R A T I O N S
  //
  //	--------------------------------------------------------------------------

  // translation function
  const { t } = useTranslation();

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  // data-management jobs.
  const [sJobIDExpanded, setJobIDExpanded] = useState<string[]>([]);
  const [sJobRenderCount, setJobRenderCount] = useState<number>(0);

  // build list of 'to' storage areas.
  var toStorageArea: string[] = [];
  for (var i = 0; i < props.dataManagementJobs.length; i++)
    toStorageArea.push(
      getStorageAreaFromID({
        storageAreaUUID: props.dataManagementJobs[i].to_storage_area_uuid,
      }),
    );

  //	--------------------------------------------------------------------------
  //
  //	F U N C T I O N S
  //
  //	--------------------------------------------------------------------------

  //	-------------------------------------------------
  //
  //	hide the failed jobs in the jobs panel.
  //
  //	-------------------------------------------------

  async function clearFailedJobs() {
    console.log('clearFailedJobs fires:');

    // build the url.
    var urlCommand: string = APIPrefix() + '/v1/data_management/clear_failed_jobs';

    try {
      const apiResult = await fetch(urlCommand, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      // get the list of data-management jobs.
      const newEvent: Event = {
        eventTarget: EventTarget.APP,
        eventType: EventType.GET_DATA_MANAGEMENT_JOBS,
      };
      dispatch(
        handleEvent({
          event: newEvent,
        }),
      );
      //
    } catch (e) {}
  } // clearFailedJobs

  //	-------------------------------------------------
  //
  //	extract a list of local compute services for the
  //	given site.
  //
  //	-------------------------------------------------

  function getAssociatedServices(args: { site_uuid: string }) {
    //
    let computeServices: ComputeServiceType[] = [];

    // loop through the compute services to find items for this site.
    for (let index = 0; index < sD_computeServices.length; index++) {
      //
      if (
        sD_computeServices[index].site.site_uuid === args.site_uuid &&
        sD_computeServices[index].health_status?.toUpperCase() === 'ACTIVE'
      ) {
        computeServices.push(sD_computeServices[index]);
      }
      //
    }

    // return something.
    return computeServices;
    //
  } // getAssociatedServices

  //	-------------------------------------------------
  //
  //	search through the storages state array to find this uuid.
  //
  //	-------------------------------------------------

  function getStorageAreaFromID(args: { storageAreaUUID: string }) {
    //
    var storageArea: string = args.storageAreaUUID;

    for (var site = 0; site < sD_storageAreas.length; site++)
      for (var area = 0; area < sD_storageAreas[site].storage_areas.length; area++)
        if (sD_storageAreas[site].storage_areas[area].storage_id === args.storageAreaUUID)
          storageArea =
            siteName({ site: sD_storageAreas[site].site }) +
            ' -> ' +
            sD_storageAreas[site].storage_areas[area].name;

    // return something.
    return storageArea;
    //
  } // getStorageAreaFromID

  //	------------------------------------------------------------
  //
  //	search the compute API for compute resources to process
  //	these data.
  //
  //	------------------------------------------------------------

  const initiateComputeSearchHandler = (
    event: React.MouseEvent<HTMLButtonElement>,
    pToSite: string,
  ) => {
    // raise an initiate-compute-search event.
    const params: InitiateComputeSearchParams = {
      site: pToSite,
      serviceType: 'jupyterhub,canfar',
      showPage: true,
    };
    const newEvent: Event = {
      eventTarget: EventTarget.SEARCH_COMPUTE_TABLE,
      eventType: EventType.INITIATE_COMPUTE_SEARCH,
      parameters: params,
    };
    dispatch(
      handleEvent({
        event: newEvent,
      }),
    );
  }; // initiateComputeSearchHandler

  //	------------------------------------------------------------
  //
  //	search the data-management API for the files being
  //	transferred by this job ID.
  //
  //	------------------------------------------------------------

  const initiateDMSearchHandler = (
    event: React.MouseEvent<HTMLButtonElement>,
    pJobID: string,
  ) => {
    // run the data-management search.
    const params: InitiateDMSearchParams = {
      filename: '',
      jobID: pJobID,
      fileType: 'all',
      pageDisplayed: FileBrowserPage.JobDetails,
      showPage: true,
    };
    const newEvent: Event = {
      eventTarget: EventTarget.DATA_MANAGEMENT_TABLE,
      eventType: EventType.INITIATE_DM_SEARCH,
      parameters: params,
    };
    dispatch(
      handleEvent({
        event: newEvent,
        ctx: { navigate },
      }),
    );
  }; // initiateDMSearchHandler

  //	------------------------------------------------------------
  //
  //	Handler for DIV clicks.
  //
  //	------------------------------------------------------------

  const onClickDivHandler = (event: React.MouseEvent<HTMLElement>) => {
    const divElement = event.currentTarget;

    // click to expand a job.
    if (divElement.id.length > 7)
      if (divElement.id.slice(0, 7) === 'expand_') {
        // get the ID and convert to numeric.
        const divIDstr: string = divElement.id.slice(7 - divElement.id.length);
        var jobIndex: number = -1;
        try {
          jobIndex = Number(divIDstr);
        } catch {}

        // get a copy of the expanded jobs list.
        var jobIDs: string[] = sJobIDExpanded;

        // check if this job ID already exists in the expanded jobs list.
        const index: number = jobIDs.indexOf(props.dataManagementJobs[jobIndex].job_id);
        if (index > -1)
          // remove the job ID from the list of expanded jobs.
          jobIDs.splice(index, 1);
        else
          // add the job ID to the list of expanded jobs.
          jobIDs.push(props.dataManagementJobs[jobIndex].job_id);

        // update the state.
        setJobIDExpanded(jobIDs);
        setJobRenderCount(sJobRenderCount + 1);
      }
    //
  }; // onClickDivHandler

  //	------------------------------------------------------------
  //
  //	Handler for changes to the radio buttons.
  //
  //	------------------------------------------------------------

  const onClickHandler = (event: React.MouseEvent<HTMLElement>) => {
    const inputBox = event.currentTarget;

    // refresh jobs is pressed, so get a list of jobs from the API.
    if (inputBox.id === 'refresh-page') {
      //
      // get the list of data-management jobs.
      const newEvent: Event = {
        eventTarget: EventTarget.APP,
        eventType: EventType.GET_DATA_MANAGEMENT_JOBS,
      };
      dispatch(
        handleEvent({
          event: newEvent,
        }),
      );
      //
    }

    // clear the failed jobs.
    if (inputBox.id === 'clear-failed-jobs') {
      clearFailedJobs();
    }
    //
  }; // onClickHandler

  //	--------------------------------------------------------------------------
  //
  //	C O M P O N E N T S
  //
  //	--------------------------------------------------------------------------

  //	-------------------------------------------------
  //
  //	Display a single item.
  //
  //	-------------------------------------------------

  function Item(args: { item: JobItemType; type: string; expanded: boolean }) {
    return (
      <tr className="text-colour" hidden={args.expanded === false}>
        <td
          style={{
            margin: '0px 5px 0px 5px',
            borderStyle: 'none',
            wordBreak: 'break-word',
          }}
        >
          {args.item.name}
        </td>
        <td
          style={{ margin: '0px 5px 0px 5px', borderStyle: 'none', textAlign: 'center' }}
        >
          {args.type}
        </td>
        <td
          style={{ margin: '0px 5px 0px 5px', borderStyle: 'none', textAlign: 'center' }}
        >
          {DisplayFileSize({ bytes: args.item.bytes })}
        </td>
      </tr>
    );
  } // Item

  const DataManagementJobItem = (args: { item: JobType; index: number }) => {
    const [LogEntryIndex, setLogEntryIndex] = useState<number | undefined>(undefined);

    const getLogIndexByJobID = (JobID: string) => {
      if (props.logEntries.length < 1) return undefined;
      // Get the index of the log entry for the error message. Start searching from
      // the end of the logs list as the logs are ordered descending in time. The
      // earliest entry for a job is used for the error message.
      for (var i: number = props.logEntries.length - 1; i >= 0; i--) {
        if (props.logEntries[i].metadata.job_id === undefined) continue;
        if (props.logEntries[i].metadata.job_id === JobID)
          return props.logEntries.length - 1 - i;
      }
      return undefined;
    };

    useEffect(() => {
      // Only set the associated log message index if there is an error message.
      if (args.item.error_message === undefined) return;
      setLogEntryIndex(getLogIndexByJobID(args.item.job_id));
    }, [props.logEntries, args.item.error_message]);

    const onClickHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
      if (LogEntryIndex === undefined) return;
      props.logsFocusRow.current = `log_table_row_${LogEntryIndex}`;
      navigate(toolTypeToPath[ToolType.LOGS]);
    };

    return (
      <>
        <div
          key={args.index}
          style={{
            flex: '0 0 auto',
            width: '100%',
            display: 'flex',
            marginTop: '10px',
            flexDirection: 'column',
          }}
        >
          {/* the job header row */}
          <button
            aria-expanded={sJobIDExpanded.indexOf(args.item.job_id) > -1}
            aria-controls={'data-transfer-job-' + args.index.toString()}
            style={{
              flex: '0 0 auto',
              display: 'flex',
              flexDirection: 'row',
              cursor: 'pointer',
              alignItems: 'center',
              border: 'none',
              backgroundColor: 'transparent',
            }}
            id={'expand_' + args.index.toString()}
            onClick={onClickDivHandler}
          >
            <div style={{ flex: '0 0 5px' }} />

            {/* an up or down arrow that shows whether the job details are expanded */}
            <div
              className="mini-button-background"
              style={{
                flex: '0 0 21px',
                textAlign: 'center',
                alignItems: 'center',
                marginTop: '0px',
                borderRadius: '10px',
                height: '21px',
              }}
            >
              <img
                className="job-status-icon-dark"
                src={
                  sJobIDExpanded.indexOf(args.item.job_id) > -1
                    ? sG_darkMode === true
                      ? upArrowDarkIcon
                      : upArrowLightIcon
                    : sG_darkMode === true
                      ? downArrowDarkIcon
                      : downArrowLightIcon
                }
                alt={
                  sJobIDExpanded.indexOf(args.item.job_id) > -1
                    ? 'Collapse job'
                    : 'Expand job'
                }
                width="15"
                height="15"
              />
            </div>
            <div style={{ flex: '0 0 5px' }} />

            {/* the highlighted part of the job row */}
            <div
              className="job-row"
              style={{
                flex: '1 1 auto',
                display: 'flex',
                flexDirection: 'row',
                borderRadius: '10px',
                alignItems: 'center',
                height: 'auto',
                marginRight: '10px',
              }}
            >
              <div style={{ flex: '0 0 10px' }} />
              <div
                className="text-colour"
                style={{
                  flex: '1 1 auto',
                  margin: '0px 0px 0px 0px',
                }}
              >
                {args.item.num_items} item(s) to {toStorageArea[args.index]}
              </div>
              <div style={{ flex: '0 0 5px' }} />

              {/* the indicator of the job status, i.e. MOVING, READY, etc */}
              <StatusIndicator status={args.item.status} />

              <div style={{ flex: '0 0 5px' }} />
            </div>
          </button>

          {/* the View data and Process data buttons. */}
          <div style={{ flex: '0 0 auto', display: 'flex', flexDirection: 'row' }}>
            <div style={{ flex: '0 0 35px' }} />
            <div style={{ flex: '0 0 auto' }}>
              <button
                type="button"
                className="menu-button-text text-colour"
                title="View data"
                style={{
                  flex: '0 0',
                  cursor: 'pointer',
                  margin: '0px',
                  backgroundColor: 'transparent',
                }}
                onClick={(event) =>
                  initiateDMSearchHandler(
                    /* event = */ event,
                    /* pJobID = */ args.item.job_id,
                  )
                }
                data-align="R"
              >
                View data
              </button>
            </div>
            <div style={{ flex: '0 0 5px' }} />
            <div
              style={{
                display:
                  args.item.status.toUpperCase() === 'COMPLETED' ? 'block' : 'none',
              }}
            >
              <button
                type="button"
                className="menu-button-text text-colour"
                title="Process data"
                style={{
                  flex: '0 0',
                  cursor: 'pointer',
                  margin: '0px',
                  backgroundColor: 'transparent',
                }}
                onClick={(event) =>
                  initiateComputeSearchHandler(
                    /* event = */ event,
                    /* pToSite = */ args.item.to_site,
                  )
                }
                data-align="R"
              >
                Process data
              </button>
            </div>
            <div style={{ flex: '0 0 5px' }} />
          </div>
          <div style={{ flex: '0 0 5px' }} />
          <div
            id={'data-transfer-job-' + args.index.toString()}
            style={{ display: 'flex', flexDirection: 'column' }}
          >
            {/* table showing all the containers being moved */}
            <div style={{ display: 'flex', flexDirection: 'row' }}>
              <div style={{ flex: '0 0 35px' }} />
              <div style={{ flex: '1 1' }}>
                <table style={{ width: '100%' }}>
                  <tbody>
                    {args.item.containers.map((container, containerIndex) => (
                      <Item
                        key={containerIndex}
                        item={container}
                        type="CONTAINER"
                        expanded={sJobIDExpanded.indexOf(args.item.job_id) > -1}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ flex: '0 0 5px' }} />
            </div>

            {/* table showing all the datasets being moved */}
            <div style={{ display: 'flex', flexDirection: 'row' }}>
              <div style={{ flex: '0 0 35px' }} />
              <div style={{ flex: '1 1' }}>
                <table style={{ width: '100%' }}>
                  <tbody>
                    {args.item.datasets.map((dataset, datasetIndex) => (
                      <Item
                        key={datasetIndex}
                        item={dataset}
                        type="DATASET"
                        expanded={sJobIDExpanded.indexOf(args.item.job_id) > -1}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ flex: '0 0 5px' }} />
            </div>

            {/* table showing all the files being moved */}
            <div style={{ display: 'flex', flexDirection: 'row' }}>
              <div style={{ flex: '0 0 35px' }} />
              <div style={{ flex: '1 1' }}>
                <table style={{ width: '100%' }}>
                  <tbody>
                    {args.item.files.map((file, fileIndex) => (
                      <Item
                        key={fileIndex}
                        item={file}
                        type="FILE"
                        expanded={sJobIDExpanded.indexOf(args.item.job_id) > -1}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ flex: '0 0 5px' }} />
            </div>

            {/* Error message for failed jobs. */}
            <div
              className="text-colour"
              style={{
                flex: '0 0 10px',
                display:
                  sJobIDExpanded.indexOf(args.item.job_id) > -1 &&
                  args.item.error_message !== undefined
                    ? 'flex'
                    : 'none',
                flexDirection: 'row',
                width: '100%',
                paddingTop: '10px',
              }}
            >
              <div style={{ flex: '0 0 30px' }} />
              <div style={{ width: '100%' }}>
                <div
                  style={{
                    width: '100%',
                    borderBottom: 'solid 5px transparent',
                  }}
                >
                  Error Details:
                  <br />
                </div>
                <div
                  style={{
                    width: '100%',
                    overflowWrap: 'anywhere',
                    backgroundColor: 'rgba(0, 0, 0, 0.3)',
                    borderRadius: '10px',
                    border: 'solid 5px transparent',
                  }}
                >
                  {addLineBreaks(args.item.error_message?.message)}
                </div>
              </div>
            </div>

            <div
              style={{
                display:
                  sJobIDExpanded.indexOf(args.item.job_id) > -1 &&
                  LogEntryIndex !== undefined
                    ? 'flex'
                    : 'none',
                paddingTop: '10px',
              }}
            >
              <div style={{ flex: '0 0 30px' }} />
              <button
                type="button"
                className="menu-button-text text-colour"
                title="View log"
                style={{
                  flex: '0 0 auto',
                  cursor: 'pointer',
                  margin: '0px',
                  backgroundColor: 'transparent',
                }}
                onClick={onClickHandler}
                data-align="R"
              >
                View log
              </button>
            </div>

            <div
              className="text-colour"
              style={{
                flex: '0 0 auto',
                display:
                  sJobIDExpanded.indexOf(args.item.job_id) > -1 &&
                  args.item.status === 'COMPLETED'
                    ? 'flex'
                    : 'none',
                flexDirection: 'row',
                paddingTop: '10px',
              }}
            >
              <div style={{ flex: '0 0 35px' }} />
              <div style={{ flex: '1 1 0px' }}>Expires at: {args.item.expires_at}</div>
            </div>
            <div
              style={{
                flex: '0 0 10px',
                display: sJobIDExpanded.indexOf(args.item.job_id) > -1 ? 'flex' : 'none',
              }}
            />
            <div
              className="text-colour"
              style={{
                flex: '0 0 auto',
                display:
                  sJobIDExpanded.indexOf(args.item.job_id) > -1 &&
                  args.item.status.toUpperCase() === 'COMPLETED'
                    ? 'flex'
                    : 'none',
                flexDirection: 'row',
              }}
            >
              <div style={{ flex: '0 0 35px' }} />
              <div style={{ flex: '1 1 0px' }}>
                Quick links to local compute services:
              </div>
            </div>
            <div
              style={{
                flex: '0 0 10px',
                display: sJobIDExpanded.indexOf(args.item.job_id) > -1 ? 'flex' : 'none',
              }}
            />
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                paddingLeft: '50px',
              }}
            >
              {sJobIDExpanded.indexOf(args.item.job_id) > -1 &&
              args.item.status.toUpperCase() === 'COMPLETED' ? (
                getAssociatedServices({
                  site_uuid: args.item.to_site_uuid,
                }).map((hub, hubindex) => <ComputeSession session={hub} />)
              ) : (
                <></>
              )}
            </div>
          </div>
          <div style={{ flex: '0 0 5px' }} />
        </div>
      </>
    );
  };

  return (
    <div
      className={
        props.transparentBackground === true ? 'panel-area' : 'panel-main panel-area'
      }
      style={{
        flex: '1 1 265px',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        maxHeight: '100%',
      }}
    >
      <div className="panel-header" style={{ flex: '0 0 auto', marginBottom: '8px' }}>
        <div
          id="lbl-data-staging-requests"
          role="heading"
          aria-level={2}
          className="header-text"
          style={{ flex: '1 1 0px' }}
        >
          {t('Status of data-staging requests')}
        </div>
      </div>

      {props.dataManagementJobs.length === 0 && sG_loggedIn === true && (
        <div className="no-data-overlay">No data-staging requests</div>
      )}
      {sG_loggedIn !== true && <div className="no-data-overlay">Login required</div>}
      <Scrollbox
        direction="vertical"
        flex="1 1"
        width="100%"
        flexDirection="column"
        margin="5px"
      >
        {props.dataManagementJobs.map((job, index) => (
          <DataManagementJobItem item={job} index={index} />
        ))}
      </Scrollbox>
      <div className="form-button-container">
        <PleaseWait text="Loading jobs....." visible={props.jobsLoading} />
        <div style={{ flex: '1 1' }} />
        <MiniButton
          id="refresh-page"
          style={{ flex: '0 0 auto', height: '34px' }}
          onClick={onClickHandler}
          title={t('Refresh')}
          aria-label={t('Refresh')}
        >
          <img src={refreshIcon} role="decoration" alt="" width="34" height="34" />
        </MiniButton>
        <div style={{ flex: '0 0 15px' }} />
        <MiniButton
          id="clear-failed-jobs"
          data-selected="F"
          onClick={onClickHandler}
          title={t('Clear failed jobs')}
          aria-label={t('Clear failed jobs')}
        >
          <img
            src={sG_darkMode === true ? binDarkIcon : binLightIcon}
            role="decoration"
            alt=""
            width="28"
            height="28"
          />
        </MiniButton>
      </div>
    </div>
  );
  //
} // JobStatus
